import React, { useEffect, useMemo, useState } from 'react';
import Card from '../../../../../components/material-ui/adaptive/Card';
import ShowContainer from '../../../../../components/material-ui/ShowContainer';
import {
  Box,
  Stack,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import CusLabelSelect from '../../../../../components/material-ui/CusLabelSelect';
import CusButton from '../../../../../components/material-ui/CusButton';
import MyTableCell from '../../../../../components/material-ui/adaptive/MyTableCell';
import { styleCellTitle } from '../../../../../components/bonus/ui/styles';
import { dispatchCusEvent, getOnlyData } from '../../../../../utils';
import dayjs from 'dayjs';
import CusCheckbox from '../../../../../components/material-ui/CusCheckbox';
import AllCheckbox from '../../../../../components/material-ui/AllCheckbox';
import { useTranslation } from 'react-i18next';
import ConfirmDialog from '../../../../../components/shared/ConfirmDialog';
import CusAutocomplete from '../../../../../components/material-ui/CusAutocomplete';
import { ENUM_CUS_EVENT, ENUM_PROMO_FILL, statusOptions } from '../../../../../enums/enums';
import useToast from '../../../../../hooks/useToast';

export default function PromocodesList({
  groupId,
  chosenList = [],
  setChosenList,
  onCopyClipboard,
  onDeactivate,
  seriesOptions,
  promoUpdateTicker,
  listPromocodes,
  status,
  setStatus,
  setPromoUpdateTicker,
  onDelete
}) {
  const { t } = useTranslation();
  const { successToast } = useToast();
  const [openDelete, setOpenDelete] = useState(false);
  const [openDeactivate, setOpenDeactivate] = useState(false);
  const [curPromoList, setCurPromoList] = useState([]);
  const [curSeries, setCurSeries] = useState('');
  const [showDisable, setShowDisable] = useState(false);

  const allListId = curPromoList.map((el) => el?.[0]);

  const getData = (data) => {
    if (!data) return undefined;
    return getOnlyData(dayjs(data), '', 'DD.MM.YYYY');
  };

  const onSelectClick = (id) => {
    setChosenList((prev) => (prev?.includes(id) ? prev.filter((el) => el !== id) : [...prev, id]));
  };

  const getCurrentPromo = () => {
    if (!curSeries) {
      setCurPromoList(listPromocodes);
      return;
    }

    const promoList = listPromocodes.filter((el) => el[1]?.search(curSeries) == 0);
    setCurPromoList(promoList);
  };

  const onShowClick = () => {
    setShowDisable(true);
    dispatchCusEvent(ENUM_CUS_EVENT.getPromoList, {
      groupId,
      status: status,
      fill: ENUM_PROMO_FILL.list,
      successFn: () => {
        setShowDisable(false);
        setPromoUpdateTicker(Math.random());
      }
    });
  };

  useEffect(() => {
    if (!listPromocodes.length) return;

    getCurrentPromo();
    setChosenList([]);
  }, [promoUpdateTicker]);

  return (
    <>
      <Box display={'flex'} flexDirection={'column'} gap={1.5} mt={3} id="promocode-list">
        <Stack display={'flex'} flexDirection={'column'} gap={0.5}>
          <Typography variant="h5">{t('List of promocodes')}</Typography>
          <Grid container alignItems={'end'} spacing={1.5} mt={1}>
            <Grid size={{ xs: 6, sm: 4 }}>
              <CusAutocomplete
                label={t('Episode title')}
                options={seriesOptions}
                value={curSeries}
                onChange={(_, choseItem) => setCurSeries(choseItem?.value || '')}
                textFieldProps={{
                  onChange: (e) => setCurSeries(e.target.value)
                }}
              />
            </Grid>
            <Grid size={{ xs: 6, sm: 4 }}>
              <CusLabelSelect
                label={t('Status')}
                options={statusOptions}
                value={status}
                onChange={setStatus}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <CusButton fullWidth text={'Show'} onClick={onShowClick} disabled={showDisable} />
            </Grid>
          </Grid>
        </Stack>
        <ShowContainer condition={listPromocodes.length}>
          <Card variant="outlined">
            <TableContainer sx={{ maxHeight: 'calc(100dvh - 350px)', overflowY: 'auto' }}>
              <Table sx={{ minWidth: { xs: '700px' } }}>
                <TableHead>
                  <TableRow>
                    <MyTableCell style={{ ...styleCellTitle, padding: 0 }}>
                      <AllCheckbox
                        allListId={allListId}
                        chosenList={chosenList}
                        setChosenList={setChosenList}
                      />
                    </MyTableCell>
                    <MyTableCell style={styleCellTitle}>{t('Series')}</MyTableCell>
                    <MyTableCell style={styleCellTitle}>{t('Promocode')}</MyTableCell>
                    <MyTableCell style={styleCellTitle}>{t('Status')}</MyTableCell>
                    <MyTableCell style={styleCellTitle}>{t('Total / completed')}</MyTableCell>
                    <MyTableCell style={styleCellTitle}>{t('Created/valid until')}</MyTableCell>
                    <MyTableCell style={styleCellTitle}>
                      {t('Date of deposits from to')}
                    </MyTableCell>
                    <MyTableCell style={styleCellTitle}>
                      {t('Currency, Nominal, Wager, Cashout, Min deposit')}
                    </MyTableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {curPromoList.map((promo, indx) => (
                    <TableRow
                      key={`${promo[0]}-${indx}`}
                      onClick={() => onSelectClick(promo[0])}
                      selected={chosenList.includes(promo[0])}
                      style={{ cursor: 'pointer' }}>
                      <MyTableCell style={{ ...styleCellTitle, padding: 0 }}>
                        <CusCheckbox checked={chosenList.includes(promo[0])} />
                      </MyTableCell>
                      <MyTableCell style={styleCellTitle}>{promo[1]}</MyTableCell>
                      <MyTableCell
                        style={styleCellTitle}
                        onClick={() => {
                          navigator.clipboard.writeText(promo[2]).then((e) => {
                            successToast(t('copied to the exchange buffer', { promo: promo[2] }));
                          });
                        }}>
                        <Typography color="primary" fontWeight={600}>
                          {promo[2]}
                        </Typography>
                      </MyTableCell>
                      <MyTableCell style={styleCellTitle}>{t(promo[10])}</MyTableCell>
                      <MyTableCell style={styleCellTitle}>
                        <ShowContainer
                          condition={typeof promo[11] == 'number' && typeof promo[12] == 'number'}>
                          {promo[11]} / {promo[12]}
                        </ShowContainer>
                      </MyTableCell>
                      <MyTableCell style={styleCellTitle}>
                        {getData(promo[13])} - <br />
                        {getData(promo[14])}
                      </MyTableCell>
                      <MyTableCell style={styleCellTitle}>
                        {getData(promo[8])} - <br />
                        {getData(promo[9])}
                      </MyTableCell>
                      <MyTableCell style={styleCellTitle}>
                        <Stack display={'flex'} flexDirection={'column'} gap={0.1}>
                          <Box>{`${promo[3]}, ${promo[4]}, ${promo[5]}, ${promo[6]}, ${promo[7]}`}</Box>
                        </Stack>
                      </MyTableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <Grid container spacing={1} mt={2}>
              <Grid size={{ xs: 6, md: 4 }}>
                <CusButton
                  text={'Delete'}
                  fullWidth
                  onClick={setOpenDelete}
                  disabled={!chosenList.length}
                  color="error"
                />
              </Grid>
              <Grid size={{ xs: 6, md: 4 }}>
                <CusButton
                  text={'Deactivate'}
                  fullWidth
                  onClick={setOpenDeactivate}
                  color="secondary"
                  disabled={!chosenList.length}
                  variant={'contained'}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <CusButton
                  text={'Copy to clipboard'}
                  fullWidth
                  onClick={onCopyClipboard}
                  variant={'contained'}
                  disabled={!chosenList.length}
                />
              </Grid>
            </Grid>
          </Card>
        </ShowContainer>
      </Box>

      <ConfirmDialog
        open={openDelete}
        title={t('Confirm deletion')}
        content={t('Are you sure you want to delete this item? This action cannot be undone.')}
        onClose={() => setOpenDelete(false)}
        onConfirm={() => onDelete()}
      />
      <ConfirmDialog
        open={openDeactivate}
        confirmText="Deactivate"
        title={t('Confirm deactivate')}
        content={t('Are you sure you want to deactivate this item? This action cannot be undone.')}
        onClose={() => setOpenDeactivate(false)}
        onConfirm={() => onDeactivate()}
      />
    </>
  );
}
